import { useId, useLayoutEffect, useRef, type ComponentProps, type CSSProperties, type ReactNode } from 'react'
import { twMerge } from 'tailwind-merge'
import { Button } from '@highlight/ui'
import type { LineupPlanId } from '@highlight/common'
import { planContinueClasses, planToken, SOLID_CTA_BORDER_CLASSES } from './plan-select-cards'

/**
 * Each copy of the page blurs double the last over a band starting 12% lower, so
 * the head stays legible, the body softens, and the foot is a haze the notice
 * reads on. Copies rather than a masked backdrop-filter, which blurs the whole
 * box in Chromium.
 */
const BLUR_STEPS_PX = [2, 4, 8, 16, 32]

function blurStepStyle(blurPx: number, step: number): CSSProperties {
  const from = `${5 + step * 12}%`
  const mask = `linear-gradient(to bottom, transparent ${from}, #000 calc(${from} + 20%))`
  return { filter: `blur(${blurPx}px)`, maskImage: mask, WebkitMaskImage: mask }
}

/** React 18 has no `inert` prop. */
const makeInert = (node: HTMLElement | null) => node?.setAttribute('inert', '')

/**
 * Keeps each blur layer a picture of the page: its markup copied, not the page
 * mounted again, so the page's hooks and state run once. Ids are dropped from
 * the copies so they never shadow the page's own.
 */
function useMirroredPage() {
  const pageRef = useRef<HTMLDivElement | null>(null)
  const layerRefs = useRef<Array<HTMLDivElement | null>>([])
  useLayoutEffect(() => {
    const page = pageRef.current
    if (!page) return
    const mirror = () => {
      for (const layer of layerRefs.current) {
        if (!layer) continue
        layer.innerHTML = page.innerHTML
        layer.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'))
      }
    }
    mirror()
    const observer = new MutationObserver(mirror)
    observer.observe(page, { subtree: true, childList: true, characterData: true, attributes: true })
    return () => observer.disconnect()
  }, [])
  return {
    pageRef,
    layerRef: (step: number) => (node: HTMLDivElement | null) => {
      layerRefs.current[step] = node
      makeInert(node)
    },
  }
}

interface LockedPageProps {
  /** The page as it would be. Drawn once and pictured under each blur step, and never interactive. */
  children: ReactNode
  /** The fact and the way on, over the haze at the foot. */
  notice?: ReactNode
  className?: string
}

/** A page the plan holds back: in view but blurred out progressively toward its foot. */
export function LockedPage({ children, notice, className }: LockedPageProps) {
  const { pageRef, layerRef } = useMirroredPage()
  return (
    <div className={twMerge('relative flex min-h-0 flex-1 flex-col overflow-hidden', className)}>
      <div
        ref={(node) => {
          pageRef.current = node
          makeInert(node)
        }}
        className="flex min-h-0 flex-1 select-none flex-col"
      >
        {children}
      </div>
      {BLUR_STEPS_PX.map((blurPx, step) => (
        <div
          key={blurPx}
          ref={layerRef(step)}
          aria-hidden="true"
          className="bg-background absolute inset-0 flex select-none flex-col"
          style={blurStepStyle(blurPx, step)}
        />
      ))}
      {notice}
    </div>
  )
}

/** The tile's glyph in the ink of the plan the notice sells. */
const TILE_INK: Record<ReturnType<typeof planToken>, string> = {
  brand: 'text-brand',
  'premium-green': 'text-success',
  'premium-blue': 'text-info',
}

interface LockNoticeProps {
  icon: ReactNode
  /** The plan the action sells or refills, which inks the tile. Pro's green when absent. */
  plan?: LineupPlanId
  title: ReactNode
  description: ReactNode
  actions?: ReactNode
  className?: string
}

/** The notice at a locked page's foot: a tile, the fact, a sentence, and one way on. */
export function LockNotice({ icon, plan = 'pro2', title, description, actions, className }: LockNoticeProps) {
  const titleId = useId()
  return (
    <section
      aria-labelledby={titleId}
      className={twMerge(
        'absolute inset-x-0 bottom-0 flex flex-col items-center px-6 pb-8 pt-6 text-center',
        className,
      )}
    >
      <span
        aria-hidden="true"
        className={twMerge(
          'bg-background corner-smooth flex size-10 items-center justify-center rounded-lg shadow-sm',
          TILE_INK[planToken(plan)],
        )}
      >
        {icon}
      </span>
      <div className="mt-4 flex max-w-[32ch] flex-col gap-1">
        <h3 id={titleId} className="type-ui-md text-foreground m-0 text-balance font-semibold">
          {title}
        </h3>
        <p className="type-prose-sm text-foreground-secondary m-0 text-balance">{description}</p>
      </div>
      {actions && <div className="mt-4 flex gap-2">{actions}</div>}
    </section>
  )
}

interface PlanUpgradeButtonProps extends Omit<ComponentProps<typeof Button>, 'variant'> {
  /** The plan it sells. The button wears its colour. */
  plan: LineupPlanId
}

/** The solid upgrade CTA in the colour of the plan it sells. */
export function PlanUpgradeButton({ plan, className, children = 'Upgrade', ...props }: PlanUpgradeButtonProps) {
  return (
    <Button size="md" className={twMerge(planContinueClasses(plan), SOLID_CTA_BORDER_CLASSES, className)} {...props}>
      {children}
    </Button>
  )
}
